import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { printResultDocument, printResultDocuments, downloadResultPdf, downloadResultsPdf, DOC_THEMES } from "@/lib/resultDocument";
import TytCalculator from "@/features/TytCalculator";
import TytParams from "@/features/TytParams";
import { Loader2, FileCheck2, Printer, FileDown, Search, Calculator, Settings2, FileBadge, RefreshCw, Trash2 } from "lucide-react";

const TABS = [
  { id: "docs", label: "Sonuç Belgeleri", icon: FileBadge },
  { id: "calc", label: "TYT Puan Hesaplama", icon: Calculator },
  { id: "params", label: "Puan Parametreleri", icon: Settings2 },
];

export default function ReportCards() {
  const [tab, setTab] = useState("docs");
  return (
    <div className="space-y-5" data-testid="report-cards">
      <div>
        <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><FileBadge size={13} /> Karne & Sonuç Belgeleri</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mt-1">Karne Merkezi</h1>
        <p className="text-slate-500 text-sm mt-1">Sınav sonuçlarını onayla, türüne göre (TYT / AYT) belgeleri toplu yazdır veya PDF indir; ÖSYM kılavuzuna göre TYT puanı hesapla.</p>
      </div>
      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`karne-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${tab === t.id ? "border-indigo-900 text-indigo-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "docs" && <DocsTab />}
      {tab === "calc" && <TytCalculator />}
      {tab === "params" && <TytParams />}
    </div>
  );
}

function DocsTab() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [open, setOpen] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/optical-exams");
      setExams(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openExam = async (x) => {
    if (open === x.id) { setOpen(null); return; }
    setOpen(x.id);
    setRows([]);
    try {
      const { data } = await api.get(`/optical-exams/${x.id}/class-report`);
      setRows(data.rows || []);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const approveAll = async (x) => {
    if (!x.result_count) return toast.error("Bu sınav için sonuç yok");
    if (!window.confirm(`"${x.name}" — ${x.result_count} sonuç onaylanıp türüne göre belgeler oluşturulsun mu?`)) return;
    setBusy(x.id + "a");
    try {
      const { data } = await api.put(`/optical-exams/${x.id}/approve`, {});
      toast.success(`${data.updated} belge oluşturuldu`);
      await load();
      if (open === x.id) openExam({ id: null }) || openExam(x);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const bulk = async (x, mode) => {
    if (!x.approved_count) return toast.error("Onaylı belge yok — önce onaylayın");
    setBusy(x.id + mode);
    try {
      const { data } = await api.get(`/optical-exams/${x.id}/documents`);
      if (mode === "pdf") { await downloadResultsPdf(data, x.name); toast.success(`${data.length} belge PDF olarak indirildi`); }
      else { const ok = await printResultDocuments(data, x.name); if (!ok) toast.error("Pop-up engellendi"); }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const single = async (x, r, mode) => {
    setBusy(r.student_id + mode);
    try {
      const list = await api.get(`/optical-results/${r.student_id}`);
      const res = (list.data || []).find((d) => d.exam_id === x.id);
      if (!res) return toast.error("Sonuç bulunamadı");
      const { data } = await api.get(`/optical-results/${r.student_id}/document/${res.id}`);
      if (mode === "pdf") await downloadResultPdf(data);
      else await printResultDocument(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const approveOne = async (x, r) => {
    try {
      await api.put(`/optical-exams/${x.id}/approve`, { student_ids: [r.student_id] });
      toast.success(`${r.name} onaylandı`);
      const { data } = await api.get(`/optical-exams/${x.id}/class-report`);
      setRows(data.rows || []);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const removeOne = async (x, r) => {
    if (!window.confirm(`${r.name} — "${x.name}" sonucu silinecek. Emin misiniz?`)) return;
    try {
      const list = await api.get(`/optical-results/${r.student_id}`);
      const res = (list.data || []).find((d) => d.exam_id === x.id);
      if (!res) return toast.error("Sonuç bulunamadı");
      await api.delete(`/optical-results/${res.id}`);
      toast.success("Sonuç silindi");
      const { data } = await api.get(`/optical-exams/${x.id}/class-report`);
      setRows(data.rows || []);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const list = exams.filter((x) => (!typeF || x.exam_type === typeF) && (!q || x.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input className="ta-input pl-8 w-64" placeholder="Sınav ara" value={q} onChange={(e) => setQ(e.target.value)} data-testid="karne-search" />
        </div>
        <select className="ta-input w-40" value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="karne-type-filter">
          <option value="">Tüm türler</option><option value="TYT">TYT</option><option value="AYT">AYT</option><option value="Branş">Branş</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="karne-refresh"><RefreshCw size={14} /> <span className="ml-1">Yenile</span></Button>
      </div>
      {loading ? <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      : list.length === 0 ? <div className="card-ta p-10 text-center text-slate-500">Sınav bulunamadı. Önce "Canlı Sınav (Optik)" bölümünden sınav oluşturup sonuç girin.</div>
      : (
        <div className="space-y-3" data-testid="karne-exam-list">
          {list.map((x) => {
            const allOk = x.result_count > 0 && x.approved_count === x.result_count;
            return (
              <div key={x.id} className="card-ta p-4" data-testid={`karne-exam-${x.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`text-xs font-extrabold px-2.5 py-0.5 rounded-full border ${x.exam_type === "TYT" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-pink-100 text-pink-700 border-pink-200"}`}>{x.exam_type}</span>
                  <button onClick={() => openExam(x)} className="font-display font-bold text-slate-900 hover:underline text-left" data-testid={`karne-exam-toggle-${x.id}`}>{x.name}</button>
                  <span className="text-xs text-slate-500">{x.date} · {x.result_count} sonuç · <b className={allOk ? "text-indigo-700" : "text-amber-600"}>{x.approved_count || 0} onaylı</b></span>
                  <div className="ml-auto flex flex-wrap gap-2">
                    {!allOk && (
                      <Button size="sm" onClick={() => approveAll(x)} disabled={busy === x.id + "a"} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`karne-approve-all-${x.id}`}>
                        <FileCheck2 size={13} /> <span className="ml-1">Tümünü Onayla</span>
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => bulk(x, "print")} disabled={busy === x.id + "print"} className="h-8 text-xs" data-testid={`karne-bulk-print-${x.id}`}>
                      <Printer size={13} /> <span className="ml-1">Toplu Yazdır</span>
                    </Button>
                    <Button size="sm" onClick={() => bulk(x, "pdf")} disabled={busy === x.id + "pdf"} className="h-8 text-xs bg-indigo-900 hover:bg-indigo-800" data-testid={`karne-bulk-pdf-${x.id}`}>
                      {busy === x.id + "pdf" ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} <span className="ml-1">Toplu PDF İndir</span>
                    </Button>
                  </div>
                </div>
                {open === x.id && (
                  <div className="mt-3 overflow-x-auto" data-testid={`karne-rows-${x.id}`}>
                    {rows.length === 0 ? <div className="text-sm text-slate-500 py-3">Yükleniyor / sonuç yok…</div> : (
                      <table className="w-full text-sm">
                        <thead><tr className="text-xs text-slate-500 border-b"><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">Öğrenci</th><th className="px-2 py-1 text-left">Alan</th><th className="px-2 py-1 text-right">Net</th><th className="px-2 py-1 text-center">Durum</th><th className="px-2 py-1 text-right">Belge</th></tr></thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.student_id} className="border-b border-slate-100" data-testid={`karne-row-${r.student_id}`}>
                              <td className="px-2 py-1.5 font-bold text-indigo-700">{r.rank}</td>
                              <td className="px-2 py-1.5 font-medium">{r.name}</td>
                              <td className="px-2 py-1.5 text-xs text-slate-500">{r.track || "-"}</td>
                              <td className="px-2 py-1.5 text-right font-mono font-semibold">{Number(r.totals?.net || 0).toFixed(2)}</td>
                              <td className="px-2 py-1.5 text-center">
                                {r.approved ? <span className="text-[11px] font-bold text-indigo-700">Onaylı</span>
                                  : <button onClick={() => approveOne(x, r)} className="text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-0.5 rounded-full" data-testid={`karne-approve-${r.student_id}`}>Onayla</button>}
                              </td>
                              <td className="px-2 py-1.5 text-right">
                                <div className="inline-flex gap-1">
                                  <button onClick={() => single(x, r, "print")} className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50" title="Yazdır" data-testid={`karne-print-${r.student_id}`}><Printer size={13} /></button>
                                  <button onClick={() => single(x, r, "pdf")} disabled={busy === r.student_id + "pdf"} className="p-1.5 rounded-md border border-slate-200 hover:bg-slate-50" title="PDF indir" data-testid={`karne-pdf-${r.student_id}`}>{busy === r.student_id + "pdf" ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />}</button>
                                  <button onClick={() => removeOne(x, r)} className="p-1.5 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50" title="Sonucu sil" data-testid={`karne-delete-${r.student_id}`}><Trash2 size={13} /></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-slate-400">Belge renkleri: {Object.keys(DOC_THEMES).join(" · ")} — kurumsal lacivert/beyaz.</p>
    </div>
  );
}
